import { isAndroidApp } from "@/lib/platform";
import React, { useEffect, useState } from "react";
import { Bell, Wallet, CalendarDays, BellRing, BellOff, Smartphone } from "lucide-react";
import { toast } from "sonner";
import SettingsLayout from "./SettingsLayout";
import { Switch } from "@/components/ui/switch";
import { useAuth } from "@/context/AuthContext";
import api, { apiError } from "@/lib/api";
import {
  enablePush,
  PUSH_BLOCKED,
  disablePush,
  getPushConfig,
  pushSupported,
  permissionState,
  currentDeviceId,
} from "@/lib/push";

const OPTIONS = [
  {
    key: "debt_reminders",
    icon: Bell,
    label: "Debt reminders",
    desc: "Get reminded when a debt is due soon, due today or overdue.",
  },
  {
    key: "payment_updates",
    icon: Wallet,
    label: "Payment & repayment updates",
    desc: "Be notified when a repayment is recorded or a record is acknowledged.",
  },
  {
    key: "weekly_summary",
    icon: CalendarDays,
    label: "Weekly summary",
    desc: "A short weekly overview of who owes you and what's outstanding.",
  },
];

function PushCard() {
  const [state, setState] = useState("loading"); // loading|unsupported|unconfigured|blocked|enabled|off
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const refresh = async () => {
    if (!pushSupported()) return setState("unsupported");
    const cfg = await getPushConfig();
    if (!cfg.configured) return setState("unconfigured");
    const perm = permissionState();
    if (perm === "denied") return setState("blocked");
    try {
      const { data } = await api.get("/push/status");
      const mine = (data.devices || []).some((d) => d.id === currentDeviceId());
      setState(data.enabled && (perm === "granted" || mine) ? "enabled" : "off");
    } catch {
      setState("off");
    }
  };

  useEffect(() => {
    refresh();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onEnable = async () => {
    setBusy(true);
    setError("");
    try {
      await enablePush();
      setState("enabled");
    } catch (e) {
      if (e?.code === PUSH_BLOCKED) setState("blocked");
      else setError("Couldn't turn on notifications. Please try again.");
    } finally {
      setBusy(false);
    }
  };

  const onDisable = async () => {
    setBusy(true);
    try {
      await disablePush();
      setState("off");
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="push-notifications-card" className="rounded-2xl border border-border bg-card overflow-hidden">
      <div className="flex items-start gap-3 px-4 py-4">
        <div className="h-9 w-9 shrink-0 rounded-full bg-secondary flex items-center justify-center mt-0.5">
          {state === "enabled" ? <BellRing className="h-4 w-4 text-primary" /> : <Smartphone className="h-4 w-4 text-primary" />}
        </div>
        <div className="flex-1 min-w-0">
          <p className="text-sm font-semibold text-foreground">Push notifications</p>
          <p className="text-xs text-muted-foreground mt-0.5">
            Get a nudge on this device when someone acknowledges a record, reports a repayment, or a debt falls due.
          </p>

          {state === "loading" && <p className="mt-3 text-xs text-muted-foreground">Checking…</p>}
          {state === "unsupported" && (
            <p data-testid="push-state-unsupported" className="mt-3 text-xs text-muted-foreground">
              This device doesn't support push notifications. On iPhone, add Debii to your Home Screen first.
            </p>
          )}
          {state === "unconfigured" && (
            <p data-testid="push-state-unconfigured" className="mt-3 text-xs text-muted-foreground">Push notifications aren't available yet.</p>
          )}

          {(state === "off" || state === "blocked") && (
            <button data-testid="push-enable-btn" onClick={onEnable} disabled={busy}
              className="mt-3 inline-flex items-center gap-2 rounded-full bg-primary text-primary-foreground text-sm font-bold px-4 py-2 disabled:opacity-60 transition-opacity">
              <BellRing className="h-4 w-4" />
              {busy ? "Enabling…" : "Enable notifications"}
            </button>
          )}
          {state === "blocked" && !busy && (
            <p data-testid="push-state-blocked" className="mt-2 text-xs text-muted-foreground">
              {isAndroidApp()
                ? "Notifications are off for Debii. Turn them on in your phone's Settings → Apps → Debii → Notifications, then tap Enable again."
                : "Notifications are blocked in this browser. Allow them in the browser's site settings, then tap Enable again."}
            </p>
          )}
          {error && state === "off" && !busy && (
            <p data-testid="push-enable-error" className="mt-2 text-xs text-muted-foreground">{error}</p>
          )}

          {state === "enabled" && (
            <div className="mt-3 flex items-center gap-3 flex-wrap">
              <span data-testid="push-state-enabled" className="inline-flex items-center gap-1.5 rounded-full bg-emerald-50 text-emerald-700 text-xs font-bold px-3 py-1.5">
                <BellRing className="h-3.5 w-3.5" /> Enabled on this device
              </span>
              <button data-testid="push-disable-btn" onClick={onDisable} disabled={busy}
                className="inline-flex items-center gap-1.5 text-xs font-bold text-muted-foreground underline underline-offset-2 disabled:opacity-60">
                <BellOff className="h-3.5 w-3.5" />
                {busy ? "Turning off…" : "Turn off"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default function NotificationSettings() {
  const { user, refreshUser } = useAuth();
  const initial = user?.notification_prefs || {
    debt_reminders: true,
    payment_updates: true,
    weekly_summary: true,
  };
  const [prefs, setPrefs] = useState(initial);
  const [savingKey, setSavingKey] = useState(null);

  const toggle = async (key) => {
    const nextVal = !prefs[key];
    const prev = prefs;
    setPrefs({ ...prefs, [key]: nextVal });
    setSavingKey(key);
    try {
      await api.patch("/auth/notification-prefs", { [key]: nextVal });
      await refreshUser();
      toast.success("Preferences saved");
    } catch (e) {
      setPrefs(prev);
      toast.error(apiError(e));
    } finally {
      setSavingKey(null);
    }
  };

  return (
    <SettingsLayout
      title="Notifications"
      subtitle="Choose what Debii reminds you about"
      testId="notification-settings-page"
    >
      <PushCard />

      <p className="mt-6 mb-2 px-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
        What to notify me about
      </p>
      <div className="rounded-2xl border border-border bg-card divide-y divide-border overflow-hidden">
        {OPTIONS.map((o) => (
          <div key={o.key} className="flex items-start gap-3 px-4 py-4">
            <div className="h-9 w-9 shrink-0 rounded-full bg-secondary flex items-center justify-center mt-0.5">
              <o.icon className="h-4 w-4 text-primary" />
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-sm font-semibold text-foreground">{o.label}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{o.desc}</p>
            </div>
            <Switch
              data-testid={`notif-toggle-${o.key}`}
              checked={!!prefs[o.key]}
              disabled={savingKey === o.key}
              onCheckedChange={() => toggle(o.key)}
              className="mt-1"
            />
          </div>
        ))}
      </div>
      <p className="mt-4 text-xs text-muted-foreground text-center">
        These apply to push notifications and emails. Your preferences are saved to
        your account and stay the same when you sign back in.
      </p>
    </SettingsLayout>
  );
}
